'use client';
import { useState, useEffect } from 'react';
import { getVideos } from '@/lib/api';
import { Video } from '@/lib/types';
import VideoCard from '@/components/ui/VideoCard';
import TabSwitcher from '@/components/ui/TabSwitcher';
import SectionHeader from '@/components/ui/SectionHeader';
import Label from '@/components/ui/Label';
import Link from 'next/link';

const videoTabs = [
  { id: 'latest', label: 'Latest' },
  { id: 'live', label: 'Live' },
  { id: 'documentaries', label: 'Documentaries' },
  { id: 'interviews', label: 'Interviews' },
  { id: 'analysis', label: 'Analysis' },
];

const categories = ['World', 'Business', 'Sport'];

export default function VideoPage() {
  const [activeTab, setActiveTab] = useState('latest');
  const [allVideos, setAllVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadVideos() {
      setLoading(true);
      try {
        const data = await getVideos();
        setAllVideos(data);
      } catch (error) {
        console.error('Error loading videos:', error);
      } finally {
        setLoading(false);
      }
    }
    loadVideos();
  }, []);

  if (loading && allVideos.length === 0) {
    return <div className="max-w-[1400px] mx-auto px-5 py-20 text-center">Loading Videos...</div>;
  }

  const featuredVideo = allVideos[0];
  const liveVideo = allVideos.find(v => v.isLive);
  const filteredVideos = activeTab === 'live'
    ? allVideos.filter(v => v.isLive)
    : allVideos;

  return (
    <div className="max-w-[1400px] mx-auto px-5">

      {/* FEATURED VIDEO HERO */}
      {featuredVideo && (
        <section className="bg-[#0D0D0D] -mx-5 mb-8">
          <Link href={`/video/${featuredVideo.id}`} className="relative block aspect-video max-h-[520px] overflow-hidden hover:opacity-90 transition-opacity">
            <div 
              className={`${featuredVideo.imageBg?.startsWith('bg-') ? featuredVideo.imageBg : 'bg-gray-100'} w-full h-full bg-cover bg-center bg-no-repeat`} 
              style={!featuredVideo.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${featuredVideo.imageBg})` } : {}}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
            {/* Play button */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[72px] h-[72px] circle-avatar border-[3px] border-white/90 bg-black/40 hover:bg-[#C8102E] hover:border-[#C8102E] transition-all duration-200 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7 ml-1">
                <polygon points="5,3 19,12 5,21" />
              </svg>
            </div>
            {/* Bottom overlay */}
            <div className="absolute bottom-0 left-0 right-0 pb-8 px-8">
              <div className="mb-2"><Label type="video" /></div>
              <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] lg:text-[30px] text-white mb-2 max-w-[640px] leading-[1.2]">
                {featuredVideo.title}
              </h1>
              <div className="flex items-center gap-4">
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] tracking-[0.08em] uppercase text-white/70">{featuredVideo.category}</span>
                <span className="bg-black/60 text-white font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] px-2 py-0.5">{featuredVideo.duration}</span>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* TABS */}
      <TabSwitcher tabs={videoTabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* LIVE STREAM STRIP */}
      {liveVideo && (
        <div className="bg-[#1A1A1A] py-4 px-6 -mx-5 flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="w-[7px] h-[7px] circle-avatar bg-[#C8102E] animate-live-pulse shrink-0" />
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-extrabold text-[12px] tracking-[0.08em] uppercase text-white">LIVE NOW</span>
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-white/80">{liveVideo.title}</span>
          </div>
          <button className="bg-[#C8102E] text-white px-5 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] hover:bg-[#9E0C23] transition-colors shrink-0">
            Watch Live
          </button>
        </div>
      )}

      {/* LATEST VIDEOS */}
      <section className="mb-10">
        <SectionHeader title={activeTab === 'live' ? 'Live Streams' : 'Latest Videos'} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filteredVideos.slice(0, 8).map(video => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </section>

      {/* DOCUMENTARY SECTION */}
      <section className="mb-10 border-t border-[#D4D4D4] pt-8">
        <SectionHeader title="Documentaries" moreLink="/video" moreLabel="All docs →" />
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {/* Featured doc */}
          {allVideos[1] && (
            <Link href={`/video/${allVideos[1].id}`} className="group cursor-pointer hover:opacity-80 transition-opacity block">
              <div className="relative bg-[#1A1A1A] h-[300px] w-full overflow-hidden">
                <div 
                  className={`${allVideos[1].imageBg?.startsWith('bg-') ? allVideos[1].imageBg : 'bg-gray-100'} w-full h-full bg-cover bg-center bg-no-repeat`}
                  style={!allVideos[1].imageBg?.startsWith('bg-') ? { backgroundImage: `url(${allVideos[1].imageBg})` } : {}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[56px] h-[56px] circle-avatar border-[2px] border-white/80 bg-black/40 hover:bg-[#C8102E] transition-all flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5 ml-0.5">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[10px] tracking-[0.1em] uppercase text-white/70 mb-1 block">Documentary · {allVideos[1].duration}</span>
                  <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] text-white leading-[1.2]">{allVideos[1].title}</h2>
                </div>
              </div>
            </Link>
          )}
          {/* 4-item list */}
          <div className="divide-y divide-[#D4D4D4]">
            {allVideos.slice(4, 8).map(video => (
              <Link key={video.id} href={`/video/${video.id}`} className="flex gap-3 items-start py-3 group cursor-pointer hover:opacity-75 transition-opacity block">
                <div 
                  className={`${video.imageBg?.startsWith('bg-') ? video.imageBg : 'bg-gray-100'} w-24 h-16 shrink-0 relative bg-cover bg-center bg-no-repeat`}
                  style={!video.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${video.imageBg})` } : {}}
                >
                  <span className="absolute bottom-1 right-1 bg-black/80 text-white font-['IBM_Plex_Sans',system-ui,sans-serif] text-[10px] px-1">{video.duration}</span>
                </div>
                <div>
                  <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.08em] uppercase text-[#C8102E] block mb-1">{video.category}</span>
                  <h4 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{video.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BY CATEGORY */}
      {categories.map(cat => (
        <section key={cat} className="mb-10 border-t border-[#D4D4D4] pt-8">
          <SectionHeader title={`${cat} Video`} moreLink="/video" moreLabel="More →" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {allVideos.filter(v => v.category === cat).slice(0, 4).map(video => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
